import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check, Briefcase, Cpu, Layers, Users } from "lucide-react";
import { PILLARS, VALUES_MARQUEE_TAGS, HERO_IMAGE, TEAM_IMAGE } from "@/data/rapro";

const STATS = [
  { value: "Pan-India", label: "Reach" },
  { value: "10K+", label: "Vetted Profiles" },
  { value: "48hr", label: "Avg Turnaround" },
  { value: "BPO + Tech", label: "Dual Practice" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 dotted-grid opacity-40" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 md:pt-24 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7 reveal">
              <div className="label-eyebrow text-slate-500" data-testid="hero-eyebrow">
                <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
                Recruitment & Staffing · Bangalore
              </div>
              <h1 data-testid="hero-headline" className="font-display mt-6 text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]">
                Hire the <span className="relative inline-block">
                  <span className="relative z-10 text-slate-900">right people</span>
                  <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#00E676]/60 -z-0" />
                </span>
                <br />
                at the <em className="not-italic text-slate-900">right time.</em>
              </h1>
              <p className="mt-7 max-w-xl text-lg text-slate-600 leading-relaxed">
                RAPRO Solutions bridges the gap between exceptional talent and forward-thinking companies — across BPO volume hiring and high-end technology staffing.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Link
                  to="/contact"
                  data-testid="hero-primary-cta"
                  className="group inline-flex items-center gap-2 bg-[#00E676] text-slate-900 px-6 py-3.5 font-semibold hover:bg-[#00C853] transition-colors duration-150"
                >
                  Start a Hiring Plan
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center gap-2 border-2 border-slate-900 text-slate-900 px-6 py-3 font-medium hover:bg-[#00E676] hover:text-slate-900 transition-colors"
                >
                  Explore Services
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-slate-200 pt-8" data-testid="hero-stats">
                {STATS.map((s) => (
                  <div key={s.label}>
                    <div className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">{s.value}</div>
                    <div className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-500">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 reveal">
              <div className="relative">
                <div className="absolute -top-4 -left-4 h-24 w-24 diag-stripe opacity-80" />
                <div className="relative border-2 border-slate-900 hard-shadow overflow-hidden bg-white">
                  <img
                    src={HERO_IMAGE}
                    alt="Modern tech office where RAPRO matches talent with companies"
                    data-testid="hero-image"
                    className="w-full h-[420px] object-cover"
                    loading="eager"
                  />
                  <div className="p-5 flex items-center gap-3 border-t-2 border-slate-900">
                    <span className="h-2.5 w-2.5 bg-[#00E676]" />
                    <span className="text-xs uppercase tracking-[0.25em] font-bold text-slate-900">Now hiring across PAN India</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section data-testid="tech-marquee" className="border-y-2 border-slate-900 bg-[#00E676] overflow-hidden">
        <div className="py-5 flex overflow-hidden whitespace-nowrap">
          <div className="marquee-track flex items-center gap-10 shrink-0 pr-10">
            {[...VALUES_MARQUEE_TAGS, ...VALUES_MARQUEE_TAGS].map((t, i) => (
              <span key={`value-${t}-${i}`} className="font-display font-bold text-lg uppercase tracking-wider text-slate-900 flex items-center gap-10">
                {t}
                <span className="h-2 w-2 bg-slate-900 rounded-full" />
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* DUAL PRACTICE */}
      <section data-testid="practice-section" className="mx-auto max-w-7xl px-5 sm:px-8 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <div className="label-eyebrow text-slate-500">
              <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
              Two Practices, One Partner
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mt-4 text-slate-900">
              Volume hiring or niche tech — we close roles either way.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              From front-office voice teams to quantum engineers, our recruiters operate across both ends of the talent spectrum.
            </p>
            <Link
              to="/services"
              data-testid="practice-cta"
              className="mt-8 inline-flex items-center gap-2 text-slate-900 font-semibold hover:text-[#00E676] transition-colors group"
            >
              See full services
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <article data-testid="practice-bpo" className="bg-white border-2 border-slate-900 p-8 hard-shadow">
              <Briefcase className="h-8 w-8 text-slate-900" />
              <h3 className="font-display text-2xl font-bold mt-6 text-slate-900">BPO & Volume Hiring</h3>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                Bulk voice & non-voice, domestic & international, contract staffing, payroll support, walk-in and campus drives.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-slate-700">
                {["Customer Support (Voice/Chat/Email)", "Technical Support", "Back Office & Telecalling", "Multilingual & Foreign Languages"].map((i) => (
                  <li key={i} className="flex items-center gap-2"><Check className="h-4 w-4 text-[#00E676]" /> {i}</li>
                ))}
              </ul>
            </article>

            <article data-testid="practice-tech" className="bg-[#00E676] text-slate-900 border-2 border-slate-900 p-8 hard-shadow">
              <Cpu className="h-8 w-8 text-slate-900" />
              <h3 className="font-display text-2xl font-bold mt-6">High-End Technology Staffing</h3>
              <p className="mt-3 text-slate-900/80 text-sm leading-relaxed">
                Software, Cloud/DevOps, AI/Data, Cybersecurity, Blockchain, UI/UX, Networking, Automation and Emerging Tech.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-slate-900">
                {["Python · Java · Go · Rust", "AWS · Azure · GCP · Kubernetes", "ML · NLP · Big Data · MLOps", "Web3 · Solidity · Smart Contracts"].map((i) => (
                  <li key={i} className="flex items-center gap-2"><Check className="h-4 w-4 text-slate-900" /> {i}</li>
                ))}
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* WHAT SETS US APART */}
      <section data-testid="pillars-section" className="bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div className="max-w-2xl">
              <div className="label-eyebrow text-slate-500">
                <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
                What Sets Us Apart
              </div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mt-4 text-slate-900">
                Four pillars that hold every search together.
              </h2>
            </div>
            <Link
              to="/about"
              data-testid="pillars-cta"
              className="inline-flex items-center gap-2 text-slate-900 font-semibold hover:text-[#00E676] transition-colors group"
            >
              About RAPRO
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t-2 border-l-2 border-slate-900 bg-white">
            {PILLARS.map((p) => (
              <div
                key={p.num}
                data-testid={`pillar-${p.num}`}
                className="border-r-2 border-b-2 border-slate-900 p-8 group hover:bg-[#00E676] transition-colors duration-150 cursor-default"
              >
                <div className="font-display text-5xl font-extrabold text-slate-900/15 group-hover:text-slate-900/40">{p.num}</div>
                <h3 className="font-display text-xl font-bold mt-4 text-slate-900">{p.title}</h3>
                <p className="mt-3 text-sm text-slate-600 group-hover:text-slate-900 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH SPLIT */}
      <section data-testid="approach-section" className="mx-auto max-w-7xl px-5 sm:px-8 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <img
              src={TEAM_IMAGE}
              alt="Recruiters collaborating with hiring partners"
              data-testid="approach-image"
              className="w-full h-[420px] object-cover border-2 border-slate-900"
              loading="lazy"
            />
          </div>
          <div className="lg:col-span-6 lg:pl-8">
            <div className="label-eyebrow text-slate-500">
              <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
              Our Approach
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mt-4 text-slate-900">
              Proactive. Transparent. Personalised.
            </h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              We start by understanding your unique needs — culture, goals, urgency — then leverage our network, tools and insights to deliver the best talent. Whether it's a single critical role or an entire team buildout.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Users, t: "Network-led sourcing" },
                { icon: Layers, t: "Multi-stage vetting" },
                { icon: Briefcase, t: "Post-placement support" },
                { icon: Cpu, t: "Tech & BPO depth" },
              ].map((b) => (
                <div key={b.t} className="flex items-start gap-3 border-l-2 border-[#00E676] pl-4 py-2">
                  <b.icon className="h-5 w-5 text-slate-900 mt-0.5" />
                  <span className="text-sm font-medium text-slate-900">{b.t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="cta-section" className="relative bg-[#00E676]">
        <div className="absolute inset-0 dotted-grid opacity-20" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]">
                Let's build the team that builds your next chapter.
              </h2>
              <p className="mt-5 text-slate-900/80 text-lg max-w-2xl">
                Tell us about the roles you're hiring for. We'll send a tailored sourcing plan within one business day.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link
                to="/contact"
                data-testid="final-cta-button"
                className="group inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-4 font-semibold hover:bg-slate-50 transition-colors border-2 border-slate-900 hard-shadow"
              >
                Talk to a Recruiter
                <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
